import { useEffect, useLayoutEffect, useRef, useState } from "react";
import CarouselButton from '../../components/Carousel/CarouselButton';
import CarouselNavButton from '../../components/Carousel/CarouselNavButton';
import './Carousel.css';

const Carousel = ({ elements, scrollSeconds = 4 }) => {
	const [isAutoPlaying, setIsAutoPlaying] = useState(true);
	const [trackIndex, setTrackIndex] = useState(0);
	const [viewportWidth, setViewportWidth] = useState(0);
	const [maxHeight, setMaxHeight] = useState(0);
	const viewportRef = useRef(null);
	const itemRefs = useRef([]);

	// Keep track of the viewport width and tallest slide height so the carousel never clips content.
	useLayoutEffect(() => {
		const updateWidthAndHeight = () => {
			const viewport = viewportRef.current;
			if (!viewport) return;
			setViewportWidth(viewport.offsetWidth);
			const heights = itemRefs.current.filter(Boolean).map(ref => ref.offsetHeight);
			const calculatedMaxHeight = heights.length ? Math.max(...heights) : 0;
			setMaxHeight(calculatedMaxHeight);
		};

		updateWidthAndHeight();

		const resizeObserver = typeof ResizeObserver !== "undefined"
				? new ResizeObserver(() => updateWidthAndHeight())
				: null;

		itemRefs.current.forEach(ref => {
			if (ref && resizeObserver) {
				resizeObserver.observe(ref);
			}
		});

		window.addEventListener("resize", updateWidthAndHeight);
		return () => {
			if (resizeObserver) resizeObserver.disconnect();
			window.removeEventListener("resize", updateWidthAndHeight);
		};
	}, [elements]);

	// Manage an autoplay interval
	useEffect(() => {
		if (!isAutoPlaying || !scrollSeconds) return;

		const interval = setInterval(() => {
			setTrackIndex(prev => prev === elements.length - 1 ? 0 : prev + 1);
		}, scrollSeconds * 1000);

		return () => clearInterval(interval);
	}, [isAutoPlaying, elements.length, scrollSeconds]);

	const pauseAutoPlay = () => {
		console.log("Pausing carousel");
		setIsAutoPlaying(false);
	};

	const resumeAutoPlay = () => {
		console.log("Resuming carousel");
		setIsAutoPlaying(true);
	};

	const handleManualNavigation = (newIndex) => {
		pauseAutoPlay();
		setTrackIndex(newIndex);
		// Resumption is handled by onMouseLeave
		// Keyboard controls will pause it permanently, which is frankly nicer if you can't rely on a mouse
	};

	return (
		<div style={{ width: "100%", maxWidth: "100%" }} onMouseEnter={pauseAutoPlay} onTouchStart={pauseAutoPlay} onMouseLeave={resumeAutoPlay} onTouchEnd={resumeAutoPlay}>
			<div className="carousel" style={{ height: maxHeight, width: "100%", maxWidth: "100%" }}>
				<div className="buttonFlexOverlay">
					<CarouselButton onClick={() => handleManualNavigation(trackIndex === 0 ? elements.length - 1 : trackIndex - 1)}>&lt;</CarouselButton>
					<CarouselButton onClick={() => handleManualNavigation(trackIndex === elements.length - 1 ? 0 : trackIndex + 1)} rightSide={true}>&gt;</CarouselButton>
				</div>
				<div className="carouselViewport" ref={viewportRef}>
					<div className="carouselTrack" style={{ transform: `translateX(-${trackIndex * viewportWidth}px)` }}>
						{elements.map((element, index) => (
							<div key={index} className="carouselTrackItem" ref={elem => itemRefs.current[index] = elem}>
								{element}
							</div>
						))}
					</div>
				</div>
			</div>
			<div className="carouselNav">
				{elements.map((element, index) => (
					<CarouselNavButton
							key={index}
							active={index === trackIndex}
							onClick={() => handleManualNavigation(index)}>
					</CarouselNavButton>
				))}
			</div>
		</div>
	);
};

export default Carousel;